// ============================================================================
// Minimal inline Markdown renderer (no external dependency)
// Supports: bold, italic, inline code, lists, line breaks, headers
// ============================================================================

import type { ReactNode } from 'react'
import { Typography, Box } from '@mui/material'
import { beoflowColors, typography as typo } from '@/theme/muiTheme'

/**
 * Resolve a markdown link target to a safe href.
 * Allowed: external http(s) URLs and internal app-relative paths ('/...').
 * Everything else (javascript:, data:, protocol-relative '//', bare hosts)
 * is rejected — the link then renders as plain text.
 */
export function resolveMarkdownHref(url: string): { href: string; external: boolean } | null {
  const trimmed = url.trim()
  if (/^https?:\/\//i.test(trimmed)) return { href: trimmed, external: true }
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return { href: trimmed, external: false }
  return null
}

/**
 * Render inline formatting: [text](url), **bold**, *italic*, `code`.
 * Keys are scoped by `keyPrefix` so callers that splice results from multiple
 * calls into the same parent (e.g. multi-line paragraphs) don't collide.
 */
function renderInline(text: string, keyPrefix: string = 'i'): ReactNode[] {
  const nodes: ReactNode[] = []
  // Match [text](url), **bold**, *italic*, `code` — process in order of specificity
  const re = /(\[([^\]]+)\]\(([^()\s]+)\)|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let tokenIdx = 0

  while ((match = re.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    const k = `${keyPrefix}-${tokenIdx++}`
    if (match[2] && match[3]) {
      // [text](url) — safe links only; unsafe targets render as plain text
      const resolved = resolveMarkdownHref(match[3])
      if (resolved) {
        nodes.push(
          <a
            key={k}
            href={resolved.href}
            {...(resolved.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            style={{ color: beoflowColors.meanBlue, textDecoration: 'underline' }}
          >
            {match[2]}
          </a>
        )
      } else {
        nodes.push(match[2])
      }
    } else if (match[4]) {
      // **bold**
      nodes.push(<strong key={k}>{match[4]}</strong>)
    } else if (match[5]) {
      // *italic*
      nodes.push(<em key={k}>{match[5]}</em>)
    } else if (match[6]) {
      // `code`
      nodes.push(
        <code
          key={k}
          style={{
            fontFamily: typo.fontFamilyMonospace,
            fontSize: '0.9em',
            backgroundColor: 'rgba(248, 246, 244, 0.1)',
            padding: '1px 4px',
            borderRadius: '4px',
          }}
        >
          {match[6]}
        </code>
      )
    }

    lastIndex = match.index + match[0].length
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes.length > 0 ? nodes : [text]
}

/**
 * Render markdown text to React nodes.
 * Handles paragraphs, headers, unordered/ordered lists, and inline formatting.
 */
export function renderMarkdown(text: string): ReactNode {
  if (!text) return null

  const lines = text.split('\n')
  const blocks: ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Headers: ### text
    const headerMatch = line.match(/^(#{1,4})\s+(.+)$/)
    if (headerMatch) {
      const level = headerMatch[1].length
      const variant = level <= 2 ? 'h4' : level === 3 ? 'h5' : 'h6'
      blocks.push(
        <Typography key={i} variant={variant} sx={{ mt: 1.5, mb: 0.5 }}>
          {renderInline(headerMatch[2], `h-${i}`)}
        </Typography>
      )
      i++
      continue
    }

    // Unordered list: - item or * item
    if (line.match(/^\s*[-*]\s+/)) {
      const items: ReactNode[] = []
      while (i < lines.length && lines[i].match(/^\s*[-*]\s+/)) {
        const content = lines[i].replace(/^\s*[-*]\s+/, '')
        items.push(<li key={i}>{renderInline(content, `li-${i}`)}</li>)
        i++
      }
      blocks.push(
        <Box component="ul" key={`ul-${i}`} sx={{ pl: 2.5, my: 0.5, '& li': { mb: 0.25 } }}>
          {items}
        </Box>
      )
      continue
    }

    // Ordered list: 1. item
    if (line.match(/^\s*\d+\.\s+/)) {
      const items: ReactNode[] = []
      while (i < lines.length && lines[i].match(/^\s*\d+\.\s+/)) {
        const content = lines[i].replace(/^\s*\d+\.\s+/, '')
        items.push(<li key={i}>{renderInline(content, `li-${i}`)}</li>)
        i++
      }
      blocks.push(
        <Box component="ol" key={`ol-${i}`} sx={{ pl: 2.5, my: 0.5, '& li': { mb: 0.25 } }}>
          {items}
        </Box>
      )
      continue
    }

    // Empty line = paragraph break
    if (line.trim() === '') {
      i++
      continue
    }

    // Regular paragraph — collect consecutive non-empty, non-special lines
    const paraLines: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].match(/^#{1,4}\s+/) &&
      !lines[i].match(/^\s*[-*]\s+/) &&
      !lines[i].match(/^\s*\d+\.\s+/)
    ) {
      paraLines.push(lines[i])
      i++
    }

    if (paraLines.length > 0) {
      // Join with <br /> for single newlines within a paragraph
      const content: ReactNode[] = []
      paraLines.forEach((pl, idx) => {
        if (idx > 0) content.push(<br key={`br-${i}-${idx}`} />)
        content.push(...renderInline(pl, `p-${i}-${idx}`))
      })
      blocks.push(
        <Typography key={`p-${i}`} variant="body1" sx={{ mb: 0.5 }}>
          {content}
        </Typography>
      )
    }
  }

  return <>{blocks}</>
}
